import { type RefObject, useLayoutEffect, useState } from "react";

/**
 * Track the pixel width of a DOM container via ResizeObserver.
 *
 * Returns `null` until the first measurement lands so callers can render a
 * safe fallback (e.g., the legacy flex layout) instead of guessing at a
 * width before paint. After first measure, returns the current container
 * width in pixels and updates on every observed resize.
 *
 * Used by the AskPuntt resize-pane feature: only mount the PanelGroup once
 * the container is known to be ≥ 1024 px so iPad-portrait (768 px) falls
 * back to the existing flex layout rather than overflowing the viewport.
 */
export function useContainerWidth(
  ref: RefObject<HTMLElement | null>,
): number | null {
  const [width, setWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Seed immediately so callers don't flicker through a null tick when
    // the element is already laid out by the time the effect runs.
    setWidth(node.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const next = Math.round(entry.contentRect.width);
      setWidth((prev) => (prev === next ? prev : next));
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return width;
}
